import React, { useState } from "react";
import { Upload } from "@carbon/icons-react";
import "./InputImageArea.css";

export interface InputImageAreaProps {
  /**
   * Callback function when an image is selected
   */
  onChange?: (file: File | null) => void;

  /**
   * The currently selected image file
   */
  value?: File | null;

  /**
   * Whether the component is disabled
   */
  disabled?: boolean;

  /**
   * Whether there is an error
   */
  error?: boolean;

  /**
   * Error message to display
   */
  errorMessage?: string;

  /**
   * Additional CSS class
   */
  className?: string;
}

/**
 * Component for uploading and displaying images with preview
 */
export const InputImageArea: React.FC<InputImageAreaProps> = ({
  onChange,
  value,
  disabled = false,
  error = false,
  errorMessage,
  className = "",
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    value ? URL.createObjectURL(value) : null
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setPreviewUrl(URL.createObjectURL(file));
      onChange?.(file);
    }
  };

  const handleDelete = () => {
    setPreviewUrl(null);
    onChange?.(null);
  };

  const containerClasses = [
    "qida-input-image-area",
    error ? "qida-input-image-area--error" : "",
    disabled ? "qida-input-image-area--disabled" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={containerClasses}>
      {!previewUrl ? (
        <div className="qida-input-image-area__upload">
          <div className="qida-input-image-area__image-area">
            <Upload size={64} className="qida-input-image-area__icon" />
          </div>
          <label className="qida-input-image-area__button">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              disabled={disabled}
              className="qida-input-image-area__file-input"
            />
            <span className="qida-input-image-area__button-text">
              Añadir imagen
            </span>
          </label>
        </div>
      ) : (
        <div className="qida-input-image-area__preview">
          <div className="qida-input-image-area__preview-area">
            <img
              className="qida-input-image-area__preview-image"
              src={previewUrl}
              alt="Preview"
            />
            <div className="qida-input-image-area__filename">
              {value?.name || "Image preview"}
            </div>
          </div>
          <button
            className="qida-input-image-area__delete-button"
            onClick={handleDelete}
            disabled={disabled}
          >
            Borrar
          </button>
        </div>
      )}

      {error && errorMessage && (
        <div className="qida-input-image-area__error-message">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default InputImageArea;
